/**
 * useUserPageSize — shared hook to consume the user's `itemsPerPage` setting.
 *
 * Background (FINDING-S980-A, addressed S1029→S1033→S1035):
 *   /settings/general saves `itemsPerPage` to system_settings.general_display,
 *   but list pages historically hardcoded DEFAULT_PAGE_SIZE=20. This hook reads
 *   the user setting on mount and bumps the caller's pageSize state when the
 *   value is valid (in PAGE_SIZE_OPTIONS) and differs from current.
 *
 * Usage:
 *   const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
 *   useUserPageSize(pageSize, setPageSize);
 *
 * Defensive validation:
 *   - Invalid values (not in PAGE_SIZE_OPTIONS) → fall back to DEFAULT_PAGE_SIZE
 *   - Settings fetch failure → silent fallback (console.debug) — non-blocking
 *   - No needless refetch when setting matches current pageSize
 *
 * Return value (E2E UI-B B-06, 30 ก.ย. 2026):
 *   `ready` — false จนกว่าจะรู้ขนาดหน้าของผู้ใช้ (โหลดสำเร็จหรือล้มก็ถือว่ารู้แล้ว = ใช้ค่าปัจจุบัน)
 *   หน้ารายการที่ไม่อยากยิง list สองรอบ (limit ค่าเริ่มต้น → limit ของผู้ใช้) ให้รอ `ready` ก่อนยิง:
 *     const pageSizeReady = useUserPageSize(pageSize, setPageSize);
 *     useQuery({ ..., enabled: pageSizeReady })
 *   `setPageSize` กับ `ready` ถูกตั้งใน tick เดียวกัน (React batch) → render แรกที่ ready=true ได้ขนาดหน้าจริงแล้ว
 *   หน้าที่ไม่อ่านค่าคืน = พฤติกรรมเดิมทุกอย่าง
 */
import { useEffect, useState } from 'react';
import settingsService from '../services/settingsService';
import { PAGINATION_CONFIG } from '../config/pagination';

export function useUserPageSize(
  currentPageSize: number,
  setPageSize: (n: number) => void
): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const loadUserPageSize = async () => {
      try {
        const settings = await settingsService.getGeneralSettings();
        const userPref = parseInt(String(settings?.displaySettings?.itemsPerPage ?? ''), 10);
        if (
          userPref &&
          PAGINATION_CONFIG.PAGE_SIZE_OPTIONS.includes(userPref) &&
          userPref !== currentPageSize
        ) {
          setPageSize(userPref);
        }
      } catch (err) {
        console.debug('useUserPageSize: could not load user pageSize preference:', err);
      } finally {
        setReady(true);
      }
    };
    loadUserPageSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally on-mount only — settings updates require page reload
  return ready;
}

export default useUserPageSize;
